import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Tag,
  Typography,
  Space,
  Button,
  DatePicker,
  Spin,
  Alert,
} from "antd";
import {
  CalendarOutlined,
  DollarOutlined,
  HomeOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { DashboardStats, Booking } from "../../types";
import { adminOperationsService } from "../../services/adminService";
import { formatLkr } from "../../utils/currency";
import { socketService } from "../../services/socketService";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(
    null,
  );

  const [revenueData, setRevenueData] = useState<
    Array<{ name: string; revenue: number; bookings: number }>
  >([{ name: "Jan", revenue: 0, bookings: 0 }]);

  const [occupancyData, setOccupancyData] = useState<
    Array<{ name: string; occupancy: number }>
  >([]);
  const navigate = useNavigate();

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await adminOperationsService.getDashboardInsights(
        dateRange
          ? {
              startDate: dateRange[0].format("YYYY-MM-DD"),
              endDate: dateRange[1].format("YYYY-MM-DD"),
            }
          : undefined,
      );

      const payload = (response.data || {}) as any;

      const liveStats: DashboardStats = {
        totalBookings: Number(payload.totalBookings || 0),
        totalRevenue: Number(payload.totalRevenue || 0),
        occupancyRate: Number(payload.occupancyRate || 0),
        averageDailyRate: Number(payload.averageDailyRate || 0),
        todayCheckIns: Number(payload.todayCheckIns || 0),
        todayCheckOuts: Number(payload.todayCheckOuts || 0),
        availableRooms: Number(payload.availableRooms || 0),
        totalRooms: Number(payload.totalRooms || 0),
        recentBookings: payload.recentBookings || [],
        trendMetrics: payload.trendMetrics,
        weeklyOccupancy: payload.weeklyOccupancy || [],
      };

      setStats(liveStats);

      if (
        Array.isArray(payload.weeklyOccupancy) &&
        payload.weeklyOccupancy.length
      ) {
        setOccupancyData(
          payload.weeklyOccupancy.map((item: any) => ({
            name: String(item.name || "").substring(0, 3),
            occupancy: Number(item.occupancy || 0),
          })),
        );
      }

      if (
        Array.isArray(payload.monthlyRevenueTrend) &&
        payload.monthlyRevenueTrend.length
      ) {
        setRevenueData(
          payload.monthlyRevenueTrend.map((item: any) => ({
            name: String(item.name),
            revenue: Number(item.revenue || 0),
            bookings: Number(item.bookings || 0),
          })),
        );
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const socket = socketService.connect();

    const onDashboardMetricsUpdated = () => {
      fetchDashboardData();
    };

    socket.on("dashboard_metrics_updated", onDashboardMetricsUpdated);

    return () => {
      socket.off("dashboard_metrics_updated", onDashboardMetricsUpdated);
    };
  }, [fetchDashboardData]);

  const getStatusTag = (status: string) => {
    const statusColors = {
      pending: "orange",
      confirmed: "blue",
      checked_in: "green",
      checked_out: "grey",
      cancelled: "red",
      reserved: "purple",
      blocked: "volcano",
      no_show: "magenta",
    };
    return (
      <Tag color={statusColors[status as keyof typeof statusColors]}>
        {status.replace("_", " ").toUpperCase()}
      </Tag>
    );
  };

  const renderTrendNode = (trend?: number) => {
    if (trend === undefined) return null;
    const isPositive = trend >= 0;
    const color = isPositive ? "#52c41a" : "#ff4d4f";
    const Icon = isPositive ? ArrowUpOutlined : ArrowDownOutlined;

    return (
      <div style={{ marginTop: 8 }}>
        <Icon style={{ color }} />
        <Text style={{ color, marginLeft: 4 }}>
          {Math.abs(trend)}% vs previous period
        </Text>
      </div>
    );
  };

  const bookingColumns = [
    {
      title: "Reference",
      dataIndex: "booking_reference",
      key: "booking_reference",
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: "Guest Name",
      dataIndex: "guest_name",
      key: "guest_name",
    },
    {
      title: "Check-in",
      dataIndex: "check_in",
      key: "check_in",
      render: (date: string) => dayjs(date).format("MMM DD, YYYY"),
    },
    {
      title: "Guests",
      dataIndex: "guests",
      key: "guests",
    },
    {
      title: "Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount: number) => formatLkr(amount),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => getStatusTag(status),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Booking) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            const searchTerm = record.booking_reference || record.id;
            if (record.reservation_source === "walk_in") {
              navigate(`/front-desk?search=${searchTerm}`);
            } else {
              navigate(`/bookings?search=${searchTerm}`);
            }
          }}
        >
          View
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        title="Error Loading Dashboard"
        description={error}
        type="error"
        showIcon
        action={<Button onClick={fetchDashboardData}>Retry</Button>}
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space
          align="center"
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Title level={2} style={{ margin: 0 }}>
            Dashboard Overview
          </Title>
          <RangePicker
            value={dateRange}
            onChange={(dates) =>
              setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)
            }
            style={{ width: 300 }}
          />
        </Space>
      </div>

      {/* Key Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title="Total Revenue"
              value={stats?.totalRevenue}
              precision={2}
              suffix="LKR"
              valueStyle={{ color: "#52c41a", wordBreak: "break-word" }}
            />
            {renderTrendNode(stats?.trendMetrics?.revenueTrend)}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title="Total Bookings"
              value={stats?.totalBookings}
              valueStyle={{ color: "#1890ff" }}
            />
            {renderTrendNode(stats?.trendMetrics?.bookingsTrend)}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title="Occupancy Rate"
              value={stats?.occupancyRate}
              precision={1}
              suffix="%"
              valueStyle={{ color: "#722ed1" }}
            />
            {renderTrendNode(stats?.trendMetrics?.occupancyTrend)}
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card style={{ height: "100%" }}>
            <Statistic
              title="Average Daily Rate"
              value={stats?.averageDailyRate}
              precision={2}
              suffix="LKR"
              valueStyle={{ color: "#fa8c16", wordBreak: "break-word" }}
            />
            {renderTrendNode(stats?.trendMetrics?.adrTrend)}
          </Card>
        </Col>
      </Row>

      {/* Today's Overview */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={8}>
          <Card
            title="Today's Check-ins"
            size="small"
            style={{ height: "100%" }}
          >
            <Statistic
              value={stats?.todayCheckIns}
              prefix={<UserOutlined />}
              valueStyle={{ fontSize: "24px", color: "#52c41a" }}
            />
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title="Today's Check-outs"
            size="small"
            style={{ height: "100%" }}
          >
            <Statistic
              value={stats?.todayCheckOuts}
              prefix={<UserOutlined />}
              valueStyle={{ fontSize: "24px", color: "#1890ff" }}
            />
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card title="Available Rooms" size="small" style={{ height: "100%" }}>
            <Statistic
              value={stats?.availableRooms}
              suffix={`/ ${stats?.totalRooms}`}
              prefix={<HomeOutlined />}
              valueStyle={{ fontSize: "24px", color: "#722ed1" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title="Monthly Revenue Trend" size="small">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => [`LKR ${value}`, "Revenue"]} />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke="#52c41a"
                  strokeWidth={3}
                  dot={{ fill: "#52c41a" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Weekly Occupancy" size="small">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={occupancyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => [`${value}%`, "Occupancy"]} />
                <Bar dataKey="occupancy" fill="#1890ff" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Recent Bookings */}
      <Card title="Recent Bookings" size="small">
        <Table
          dataSource={stats?.recentBookings}
          columns={bookingColumns}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default Dashboard;
